// useDatabaseModalState.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Toast
import { toast } from 'react-toastify';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { SectionType } from 'utilities/Enums';
import { pluralizeTitle } from 'utilities/Languages';
import { ViewType } from 'utilities/NodeType';
import { typeHasCollectionReports } from 'collection/reports/collectionReportRegistry';
import { toLowerSnakeCase } from 'utilities/Strings';
import { NODE_MODULE, normalizeNodeModule } from 'utilities/nodeModule';
import { typeAllowsTopLevelCreate } from 'utilities/typeCreatePolicy';
import {
  typeShowsInAddMenu,
  typeShowsInMenu,
} from 'utilities/typeDisplayPolicy';
import {
  getDefaultAppearAsRows,
  getTypeAppearAsDraftRows,
  getTypeAppearAsRows,
} from 'utilities/typeAppearPolicy';
import { normalizeRollupDefinitions } from 'rollups/rollupDefinitions';
import {
  SETTINGS_TAB_ABOUT,
  SETTINGS_TAB_AUTOMATIONS,
  SETTINGS_TAB_FIELDS,
  SETTINGS_TAB_FORMS,
  SETTINGS_TAB_RELATED,
  SETTINGS_TAB_ROLLUPS,
  SETTINGS_TAB_VIEW_GROUPS,
  SETTINGS_TAB_VIEWS,
  SETTINGS_TAB_WORKFLOWS,
  normalizeSettingsTab,
} from '../databaseSettingsTabs';
import useDatabaseMissionsState from '../missions/useDatabaseMissionsState';
import { buildDatabaseAboutDuplicateState } from '../about/databaseAboutUtils';
import {
  compactRelatedEntries,
  normalizeRelatedEntries,
  normalizeTypeKeyList,
  relatedDraftListsEqual,
} from 'utilities/nodeTypeRelatedLists';
import { normalizeViewGroups } from 'utilities/ViewGroups';

// Services
import {
  deleteNodeType,
  purgeNodeTypeBaseData,
  updateNodeTypeMeta,
  updateNodeTypeRelated,
  updateNodeTypeRollups,
  updateNodeTypeViewGroups,
} from 'services/nodetypes/NodeTypeMutationService';
import {
  beginBaseTypePurge,
  endBaseTypePurge,
} from 'services/databases/systemDatabasePurgeSession';
import { updateView } from 'services/views/ViewWriteService';
import { isSystemTypeView } from 'services/views/TypeViewService';
import { isSystemTypeAutomation } from 'services/automations/TypeAutomationService';

// Utilities
import { getMovedViewPosition } from 'utilities/ViewOrder';

// Hooks
import useDatabaseAutomationsState from 'collection/automations/hooks/useDatabaseAutomationsState';
import useDatabaseFormsState from '../forms/useDatabaseFormsState';

/**
 * useDatabaseModalState
 *
 * Manages local draft state, tab selection, nested editor state, and mutation handlers for DatabaseModal.
 *
 * @param {object} params - Hook parameters.
 * @param {boolean} params.isOpen - Whether the modal is open.
 * @param {function} params.setOpen - Toggles the modal open state.
 * @param {string} params.typeKey - Active type key from database context.
 * @param {object|null} params.typeDef - Active type definition object.
 * @param {string|null} params.baseKey - Active space key.
 * @param {object|null} params.activeDatabase - Active database document data.
 * @param {string|null} params.userKey - Current user key for automation saves.
 * @param {array} params.resolvedProperties - Resolved field definitions for automation editing.
 * @param {array} params.nodeTypeOptions - Base node type options for email actions.
 * @param {function} params.onAutomationsSaved - Updates parent database state after automation saves.
 * @param {function} [params.onMissionsSaved] - Updates parent state after workflow-template saves.
 * @param {array} params.views - Custom views available for the active database.
 * @param {string|null} params.initialTab - Tab to show when the modal opens.
 * @param {object|null} params.viewEditorRequest - Optional request to open the view editor on a specific mode.
 * @param {function} params.onViewEditorRequestHandled - Clears the pending view editor request after it is consumed.
 * @param {string} params.previewNodeKeyOverride - Optional node key for form design preview.
 * @param {function} params.onSelectView - Selects a view from the settings modal.
 * @param {'private'|'public'|null} [params.nodeModule] - Active Collections/Knowledge module filter.
 *
 * @returns {object} Local modal state, computed values, and handlers.
 */
export default function useDatabaseModalState({
  isOpen,
  setOpen,
  typeKey,
  typeDef,
  baseKey,
  activeDatabase = null,
  userKey = null,
  resolvedProperties = [],
  nodeTypeOptions = [],
  onAutomationsSaved,
  onMissionsSaved,
  views = [],
  initialTab = SETTINGS_TAB_ABOUT,
  viewEditorRequest = null,
  onViewEditorRequestHandled,
  workflowEditorRequest = null,
  onWorkflowEditorRequestHandled,
  previewNodeKeyOverride = '',
  onSelectView,
  nodeModule = null,
}) {
  const { getAll } = useNodeTypes();
  const { setCurrentSection } = useNavigation();
  const navigate = useNavigate();

  const [label, setLabel] = useState('');
  const [labelPlural, setLabelPlural] = useState('');
  const [description, setDescription] = useState('');
  const [iconKey, setIconKey] = useState('');
  const [defaultView, setDefaultView] = useState(ViewType.LIST);
  const [showPeekOnOpen, setShowPeekOnOpen] = useState(true);
  const [allowTopLevelCreate, setAllowTopLevelCreate] = useState(true);
  const [showInMenu, setShowInMenu] = useState(true);
  const [showInAddMenu, setShowInAddMenu] = useState(true);
  const [appearAs, setAppearAs] = useState([]);
  const [pluralTouched, setPluralTouched] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteFinalConfirmOpen, setDeleteFinalConfirmOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(SETTINGS_TAB_ABOUT);
  const [viewEditorOpen, setViewEditorOpen] = useState(false);
  const [viewEditorMode, setViewEditorMode] = useState('create');
  const [editingView, setEditingView] = useState(null);
  const [automationEditorOpen, setAutomationEditorOpen] = useState(false);
  const [automationEditorMode, setAutomationEditorMode] = useState('create');
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [rollupEditorOpen, setRollupEditorOpen] = useState(false);
  const [editingRollup, setEditingRollup] = useState(null);
  const [rollups, setRollups] = useState([]);
  const [suggestedChildren, setSuggestedChildren] = useState([]);
  const [savingRelated, setSavingRelated] = useState(false);
  const [viewGroups, setViewGroups] = useState([]);
  const [savingViewGroups, setSavingViewGroups] = useState(false);
  const [lastModalResetKey, setLastModalResetKey] = useState('');

  const typeDefs = useMemo(() => getAll() || [], [getAll]);

  const relatedTypeOptions = useMemo(() => {
    return (typeDefs || [])
      .filter((type) => type?.enabled !== false)
      .slice()
      .sort((a, b) => {
        const left = String(a?.label || a?.key || '').toLowerCase();
        const right = String(b?.label || b?.key || '').toLowerCase();
        return left.localeCompare(right);
      });
  }, [typeDefs]);

  const effectiveTypeKey = useMemo(() => {
    return String(typeDef?.key || typeKey || '').trim();
  }, [typeDef?.key, typeKey]);

  const duplicateState = useMemo(() => buildDatabaseAboutDuplicateState({
    candidates: typeDefs,
    excludeKey: effectiveTypeKey,
    label,
    labelPlural,
  }), [effectiveTypeKey, label, labelPlural, typeDefs]);

  const canSave = useMemo(() => {
    return Boolean(String(label || '').trim())
      && !duplicateState.labelError
      && !duplicateState.pluralError
      && !saving
      && !deleting;
  }, [deleting, duplicateState.labelError, duplicateState.pluralError, label, saving]);

  const savedSuggestedChildren = useMemo(
    () => normalizeRelatedEntries(typeDef?.suggestedChildren),
    [typeDef?.suggestedChildren],
  );

  const canSaveRelated = useMemo(() => {
    if (savingRelated || deleting || saving) return false;
    return !relatedDraftListsEqual(suggestedChildren, savedSuggestedChildren);
  }, [
    deleting,
    savedSuggestedChildren,
    saving,
    savingRelated,
    suggestedChildren,
  ]);

  const savedViewGroups = useMemo(
    () => normalizeViewGroups(typeDef?.viewGroups),
    [typeDef?.viewGroups],
  );

  const canSaveViewGroups = useMemo(() => {
    if (savingViewGroups || deleting || saving) return false;
    return JSON.stringify(normalizeViewGroups(viewGroups)) !== JSON.stringify(savedViewGroups);
  }, [deleting, savedViewGroups, saving, savingViewGroups, viewGroups]);

  const isSystemType = typeDef?.system === true || typeDef?.isSystem === true;
  const settingsTabFallback = isSystemType ? SETTINGS_TAB_VIEW_GROUPS : SETTINGS_TAB_ABOUT;

  const canDelete = useMemo(() => {
    return Boolean(baseKey && effectiveTypeKey)
      && !saving
      && !deleting;
  }, [deleting, effectiveTypeKey, saving, baseKey]);

  const typeTitle = String(label || typeDef?.label || '').trim() || 'this type';
  const typeTitlePlural = String(labelPlural || typeDef?.labelPlural || '').trim()
    || (typeTitle === 'this type' ? 'records' : `${typeTitle}s`);
  const resolvedDeleteModule = normalizeNodeModule(nodeModule) || NODE_MODULE.PRIVATE;
  const deleteConfirmMessage = isSystemType
    ? `Delete "${typeTitle}"? This will permanently delete all ${typeTitlePlural} in this collection.`
    : `Delete collection "${typeTitle}"? This will permanently delete the entire ${typeTitlePlural} database and all data in it. This cannot be undone.`;
  const deleteSuccessMessage = isSystemType
    ? `${typeTitlePlural} removed.`
    : 'Database deleted.';

  const {
    sortedRules,
    isSaving: isAutomationSaving,
    fieldOptions,
    ruleFieldOptions,
    fieldTypeByKey,
    fieldEnumOptionsByKey,
    buildNewRule,
    saveRule,
    deleteRule,
  } = useDatabaseAutomationsState({
    isOpen,
    activeDatabase,
    baseKey,
    typeKey: effectiveTypeKey,
    resolvedProperties,
    userKey,
    onSaved: onAutomationsSaved,
  });

  const {
    canSaveFormSettings,
    closeFormDesign,
    createModalOpen: formCreateModalOpen,
    createTitleDraft: formCreateTitleDraft,
    editingForm,
    formDesignOpen,
    formSettingsDraft,
    formSettingsOpen,
    forms,
    handleAddForm,
    handleCreateForm,
    handleDeleteForm,
    handleReorderForm,
    handleSaveFormSettings,
    isCreating: isFormCreating,
    isSaving: isFormSaving,
    openFormDesign,
    openFormSettings,
    previewNodeKey: formsPreviewNodeKey,
    setCreateModalOpen: setFormCreateModalOpen,
    setCreateTitleDraft: setFormCreateTitleDraft,
    setFormSettingsDraft,
    setFormSettingsOpen,
  } = useDatabaseFormsState({
    isOpen,
    previewNodeKeyOverride,
    typeDef,
    typeKey: effectiveTypeKey,
    userKey,
    baseKey,
  });

  const {
    missionTemplates,
    selectedMissionTemplateKey,
    missionTasks,
    missionEdges,
    isSaving: isMissionSaving,
    missionTemplateEditorOpen,
    missionTemplateEditorMode,
    missionTitle,
    missionDescription,
    missionIconKey,
    missionMetaError,
    canSaveMissionMeta,
    setMissionTemplateEditorOpen,
    missionEditorOpen,
    setMissionEditorOpen,
    editingMissionTask,
    handleEditMissionTemplate,
    handleDesignMissionTemplate,
    handleSaveMissionDesign,
    missionDesignOpen,
    setMissionDesignOpen,
    closeMissionDesign,
    selectedMissionTemplate,
    setMissionTitle,
    setMissionDescription,
    setMissionIconKey,
    handleSaveMissionMeta,
    handleAddMissionTemplate,
    handleDeleteMissionTemplate,
    handleReorderMissionTemplate,
    handleAddMissionTask,
    handleEditMissionTask,
    handleSaveMissionTask,
    handleDeleteMissionTask,
    handleReorderMissionTask,
  } = useDatabaseMissionsState({
    activeDatabase,
    typeDef,
    isOpen,
    onSaved: onMissionsSaved,
    typeKey: effectiveTypeKey,
    baseKey,
  });

  const settingsTabs = useMemo(() => {
    // System types: schema is product-owned. Bases manage views/forms/automations
    // (system rows locked; Add still creates base-scoped artifacts).
    if (isSystemType) {
      return [
        {
          key: SETTINGS_TAB_VIEW_GROUPS,
          label: 'View Groups',
          onClick: () => setSelectedTab(SETTINGS_TAB_VIEW_GROUPS),
        },
        {
          key: SETTINGS_TAB_VIEWS,
          label: 'Views',
          onClick: () => setSelectedTab(SETTINGS_TAB_VIEWS),
        },
        {
          key: SETTINGS_TAB_FORMS,
          label: 'Forms',
          onClick: () => setSelectedTab(SETTINGS_TAB_FORMS),
        },
        {
          key: SETTINGS_TAB_WORKFLOWS,
          label: 'Playbooks',
          onClick: () => setSelectedTab(SETTINGS_TAB_WORKFLOWS),
        },
        {
          key: SETTINGS_TAB_AUTOMATIONS,
          label: 'Automations',
          onClick: () => setSelectedTab(SETTINGS_TAB_AUTOMATIONS),
        },
      ];
    }

    const tabs = [
      {
        key: SETTINGS_TAB_ABOUT,
        label: 'Edit',
        onClick: () => setSelectedTab(SETTINGS_TAB_ABOUT),
      },
      {
        key: SETTINGS_TAB_FIELDS,
        label: 'Fields',
        onClick: () => setSelectedTab(SETTINGS_TAB_FIELDS),
      },
      {
        key: SETTINGS_TAB_RELATED,
        label: 'Related',
        onClick: () => setSelectedTab(SETTINGS_TAB_RELATED),
      },
      {
        key: SETTINGS_TAB_FORMS,
        label: 'Forms',
        onClick: () => setSelectedTab(SETTINGS_TAB_FORMS),
      },
      {
        key: SETTINGS_TAB_VIEW_GROUPS,
        label: 'View Groups',
        onClick: () => setSelectedTab(SETTINGS_TAB_VIEW_GROUPS),
      },
      {
        key: SETTINGS_TAB_VIEWS,
        label: 'Views',
        onClick: () => setSelectedTab(SETTINGS_TAB_VIEWS),
      },
      {
        key: SETTINGS_TAB_ROLLUPS,
        label: 'Rollups',
        onClick: () => setSelectedTab(SETTINGS_TAB_ROLLUPS),
      },
      {
        key: SETTINGS_TAB_WORKFLOWS,
        label: 'Playbooks',
        onClick: () => setSelectedTab(SETTINGS_TAB_WORKFLOWS),
      },
      {
        key: SETTINGS_TAB_AUTOMATIONS,
        label: 'Automations',
        onClick: () => setSelectedTab(SETTINGS_TAB_AUTOMATIONS),
      },
    ];

    return tabs;
  }, [isSystemType]);

  useEffect(() => {
    if (!isOpen) return;
    const allowed = new Set(settingsTabs.map((tab) => tab.key));
    if (!allowed.has(selectedTab)) {
      setSelectedTab(settingsTabs[0]?.key || SETTINGS_TAB_ABOUT);
    }
  }, [isOpen, selectedTab, settingsTabs]);

  const modalResetKey = useMemo(() => {
    if (!isOpen) return '';
    return `${effectiveTypeKey}::${normalizeSettingsTab(initialTab, { fallback: settingsTabFallback })}`;
  }, [effectiveTypeKey, initialTab, isOpen, settingsTabFallback]);

  useEffect(() => {
    if (!isOpen) return;
    if (lastModalResetKey === modalResetKey) return;

    const nextLabel = String(typeDef?.label || '').trim();
    const nextPlural = String(typeDef?.labelPlural || '').trim();
    const expectedPlural = nextLabel ? pluralizeTitle(nextLabel) : '';
    const isAutoPlural = nextPlural && expectedPlural
      ? nextPlural.toLowerCase() === expectedPlural.toLowerCase()
      : false;

    setLabel(nextLabel);
    setLabelPlural(nextPlural || expectedPlural);
    setDescription(String(typeDef?.description || '').trim());
    setIconKey(String(typeDef?.iconKey || '').trim());
    setDefaultView(String(typeDef?.defaultView || ViewType.LIST).trim().toUpperCase() || ViewType.LIST);
    setShowPeekOnOpen(typeDef?.showPeekOnOpen !== false);
    setAllowTopLevelCreate(typeAllowsTopLevelCreate(typeDef));
    setShowInMenu(typeShowsInMenu(typeDef));
    setShowInAddMenu(typeShowsInAddMenu(typeDef));
    setAppearAs(Array.isArray(typeDef?.appearAs)
      ? getTypeAppearAsDraftRows(typeDef)
      : getDefaultAppearAsRows(typeDef));
    setPluralTouched(Boolean(nextPlural && !isAutoPlural));
    setSaveError('');
    setSaving(false);
    setDeleting(false);
    setDeleteConfirmOpen(false);
    setDeleteFinalConfirmOpen(false);
    setSelectedTab(normalizeSettingsTab(initialTab, { fallback: settingsTabFallback }));
    setViewEditorOpen(false);
    setViewEditorMode('create');
    setEditingView(null);
    setAutomationEditorOpen(false);
    setAutomationEditorMode('create');
    setEditingAutomation(null);
    setRollupEditorOpen(false);
    setEditingRollup(null);
    setRollups(normalizeRollupDefinitions(typeDef?.rollups, {
      typeKey: effectiveTypeKey,
    }));
    setSuggestedChildren(normalizeRelatedEntries(typeDef?.suggestedChildren));
    setSavingRelated(false);
    setViewGroups(normalizeViewGroups(typeDef?.viewGroups));
    setSavingViewGroups(false);
    setLastModalResetKey(modalResetKey);
  }, [
    effectiveTypeKey,
    initialTab,
    isOpen,
    lastModalResetKey,
    modalResetKey,
    settingsTabFallback,
    typeDef?.defaultView,
    typeDef?.description,
    typeDef?.iconKey,
    typeDef?.label,
    typeDef?.labelPlural,
    typeDef?.rollups,
    typeDef?.showPeekOnOpen,
    typeDef?.allowTopLevelCreate,
    typeDef?.showInMenu,
    typeDef?.showInAddMenu,
    typeDef?.appearAs,
    typeDef?.system,
    typeDef?.suggestedChildren,
    typeDef?.viewGroups,
  ]);

  useEffect(() => {
    if (isOpen) return;
    if (!lastModalResetKey) return;
    setLastModalResetKey('');
  }, [isOpen, lastModalResetKey]);

  useEffect(() => {
    if (!isOpen) return;

    setRollups(normalizeRollupDefinitions(typeDef?.rollups, {
      typeKey: effectiveTypeKey,
    }));
  }, [effectiveTypeKey, isOpen, typeDef?.rollups]);

  useEffect(() => {
    if (!isOpen) return;
    if (canSaveRelated) return;

    setSuggestedChildren(savedSuggestedChildren);
  }, [canSaveRelated, isOpen, savedSuggestedChildren]);

  useEffect(() => {
    if (!isOpen) return;
    if (canSaveViewGroups) return;

    setViewGroups(savedViewGroups);
  }, [canSaveViewGroups, isOpen, savedViewGroups]);

  useEffect(() => {
    if (!isOpen) return;
    const normalizedInitialTab = normalizeSettingsTab(initialTab, {
      fallback: settingsTabFallback,
    });
    setSelectedTab((prev) => prev === normalizedInitialTab ? prev : normalizedInitialTab);
  }, [initialTab, isOpen, settingsTabFallback]);

  useEffect(() => {
    if (pluralTouched) return;
    const cleaned = String(label || '').trim();
    if (!cleaned) {
      setLabelPlural('');
      return;
    }
    setLabelPlural(pluralizeTitle(cleaned));
  }, [label, pluralTouched]);

  useEffect(() => {
    if (!isOpen || !viewEditorRequest) return;

    const mode = String(viewEditorRequest?.mode || 'create').trim().toLowerCase();
    const isEdit = mode === 'edit';
    const seed = isEdit
      ? (viewEditorRequest?.view || null)
      : (viewEditorRequest?.draft || viewEditorRequest?.view || null);

    setSelectedTab(SETTINGS_TAB_VIEWS);
    setViewEditorMode(isEdit ? 'edit' : 'create');
    setEditingView(seed);
    setViewEditorOpen(true);
    onViewEditorRequestHandled?.();
  }, [isOpen, onViewEditorRequestHandled, viewEditorRequest]);

  useEffect(() => {
    if (!isOpen || !workflowEditorRequest) return;

    const templateKey = String(workflowEditorRequest?.templateKey || '').trim();
    if (!templateKey) {
      onWorkflowEditorRequestHandled?.();
      return;
    }

    const templates = Array.isArray(missionTemplates) ? missionTemplates : [];
    const template = templates
      .find((row) => String(row?.key || '').trim() === templateKey)
      || null;

    // Wait until workflow templates have loaded for this collection.
    if (!template) {
      if (templates.length > 0) {
        onWorkflowEditorRequestHandled?.();
      }
      return;
    }

    setSelectedTab(SETTINGS_TAB_WORKFLOWS);
    handleDesignMissionTemplate(template);
    onWorkflowEditorRequestHandled?.();
  }, [
    handleDesignMissionTemplate,
    isOpen,
    missionTemplates,
    onWorkflowEditorRequestHandled,
    workflowEditorRequest,
  ]);

  /**
   * handleIconKeyChange
   *
   * Normalizes and updates the active database icon key.
   *
   * @param {string} nextKey - Next icon key value.
   */
  const handleIconKeyChange = useCallback((nextKey) => {
    setIconKey(toLowerSnakeCase(nextKey));
  }, []);

  /**
   * handleLabelPluralChange
   *
   * Updates plural label and marks manual override state.
   *
   * @param {string} nextValue - Next plural label value.
   */
  const handleLabelPluralChange = useCallback((nextValue) => {
    setPluralTouched(true);
    setLabelPlural(nextValue);
  }, []);

  /**
   * handleOpenDeleteConfirm
   *
   * Opens the first-step delete confirmation dialog.
   */
  const handleOpenDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  /**
   * handleSaveRelated
   *
   * Persists related type settings for the active collection.
   *
   * @returns {Promise<void>} Resolves when the save flow completes.
   */
  const handleSaveRelated = useCallback(async () => {
    if (savingRelated || deleting || saving) return;
    if (!effectiveTypeKey) {
      toast.error('Type is not ready yet.');
      return;
    }

    const cleanedSuggested = compactRelatedEntries(suggestedChildren);

    setSavingRelated(true);

    try {
      await updateNodeTypeRelated({
        baseKey: baseKey || null,
        typeKey: effectiveTypeKey,
        suggestedChildren: cleanedSuggested,
      });
      setSuggestedChildren(normalizeRelatedEntries(cleanedSuggested));
      toast.success('Related settings updated.');
    } catch (error) {
      toast.error(error?.message || 'Could not update related settings.');
    } finally {
      setSavingRelated(false);
    }
  }, [
    baseKey,
    deleting,
    effectiveTypeKey,
    saving,
    savingRelated,
    suggestedChildren,
  ]);

  /**
   * handleSaveViewGroups
   *
   * Persists view group settings for the active collection.
   *
   * @returns {Promise<void>}
   */
  const handleSaveViewGroups = useCallback(async () => {
    if (savingViewGroups || deleting || saving) return;
    if (!effectiveTypeKey) {
      toast.error('Type is not ready yet.');
      return;
    }

    const cleanedGroups = normalizeViewGroups(viewGroups);

    setSavingViewGroups(true);

    try {
      await updateNodeTypeViewGroups({
        baseKey: baseKey || null,
        typeKey: effectiveTypeKey,
        viewGroups: cleanedGroups,
      });
      setViewGroups(cleanedGroups);
      toast.success('View groups updated.');
    } catch (error) {
      toast.error(error?.message || 'Could not update view groups.');
    } finally {
      setSavingViewGroups(false);
    }
  }, [
    baseKey,
    deleting,
    effectiveTypeKey,
    saving,
    savingViewGroups,
    viewGroups,
  ]);

  /**
   * handleSave
   *
   * Persists type metadata updates.
   *
   * @returns {Promise<void>} Resolves when the save flow completes.
   */
  const handleSave = useCallback(async () => {
    if (saving || deleting) return;
    if (!effectiveTypeKey) {
      toast.error('Type is not ready yet.');
      return;
    }

    if (duplicateState.labelError || duplicateState.pluralError) {
      setSaveError(duplicateState.labelError || duplicateState.pluralError);
      return;
    }

    const cleanedLabel = String(label || '').trim();
    const cleanedPlural = String(labelPlural || '').trim() || cleanedLabel;
    const cleanedIconKey = toLowerSnakeCase(iconKey);

    if (!cleanedLabel) {
      toast.error('Singular label is required.');
      return;
    }

    setSaving(true);
    setSaveError('');

    try {
      await updateNodeTypeMeta({
        baseKey: baseKey || null,
        typeKey: effectiveTypeKey,
        label: cleanedLabel,
        labelPlural: cleanedPlural,
        description,
        iconKey: cleanedIconKey,
        defaultView,
        showPeekOnOpen: showPeekOnOpen !== false,
        allowTopLevelCreate: allowTopLevelCreate !== false,
        showInMenu: showInMenu !== false,
        showInAddMenu: showInAddMenu !== false,
        appearAs: getTypeAppearAsRows(appearAs),
      });
      toast.success('Type settings updated.');
      setOpen(false);
    } catch (error) {
      toast.error(error?.message || 'Could not update type settings.');
    } finally {
      setSaving(false);
    }
  }, [
    allowTopLevelCreate,
    showInMenu,
    showInAddMenu,
    appearAs,
    defaultView,
    deleting,
    description,
    duplicateState.labelError,
    duplicateState.pluralError,
    effectiveTypeKey,
    iconKey,
    label,
    labelPlural,
    saving,
    setOpen,
    baseKey,
    showPeekOnOpen,
  ]);

  /**
   * handleDelete
   *
   * Deletes the active database payload and all rows for its type in this base.
   *
   * @returns {Promise<void>} Resolves when the delete flow completes.
   */
  const handleDelete = useCallback(async () => {
    if (!canDelete) return;

    const module = resolvedDeleteModule;
    if (!module) {
      toast.error('Could not delete this collection. Try again.');
      return;
    }

    setDeleteFinalConfirmOpen(false);
    setDeleteConfirmOpen(false);
    setDeleting(true);
    setSaveError('');
    beginBaseTypePurge(baseKey);

    try {
      const result = isSystemType
        ? await purgeNodeTypeBaseData({
          baseKey,
          typeKey: effectiveTypeKey,
          deleteNodes: true,
          module,
        })
        : await deleteNodeType({
          baseKey,
          typeKey: effectiveTypeKey,
          deleteNodes: true,
          module,
        });
      const deletedCount = Number(result?.deletedNodeCount || 0);
      toast.success(`${deleteSuccessMessage} Removed ${deletedCount} record${deletedCount === 1 ? '' : 's'}.`);
      setDeleteFinalConfirmOpen(false);
      setOpen(false);
      setCurrentSection(SectionType.HOME);
      navigate('/');
    } catch (error) {
      toast.error(error?.message || 'Could not delete collection.');
    } finally {
      endBaseTypePurge(baseKey);
      setDeleting(false);
    }
  }, [
    canDelete,
    deleteSuccessMessage,
    effectiveTypeKey,
    isSystemType,
    navigate,
    resolvedDeleteModule,
    setCurrentSection,
    setOpen,
    baseKey,
  ]);

  /**
   * handleAddView
   *
   * Opens the nested create-view modal.
   */
  const handleAddView = useCallback(() => {
    setViewEditorMode('create');
    setEditingView(null);
    setViewEditorOpen(true);
  }, []);

  /**
   * handleEditView
   *
   * Opens the nested edit-view modal.
   *
   * @param {object|null} nextView - The view to edit.
   */
  const handleEditView = useCallback((nextView) => {
    if (!nextView) return;
    if (isSystemTypeView(nextView)) return;
    setViewEditorMode('edit');
    setEditingView(nextView);
    setViewEditorOpen(true);
  }, []);

  /**
   * handleViewSaved
   *
   * Syncs selection after the nested view editor saves.
   *
   * @param {object|null} savedView - Newly created or updated view.
   */
  const handleViewSaved = useCallback((savedView) => {
    const savedViewKey = String(savedView?.key || '').trim();
    if (savedViewKey) {
      onSelectView?.(savedViewKey);
    }
    setViewEditorOpen(false);
  }, [onSelectView]);

  /**
   * handleAddAutomation
   *
   * Opens the create-automation modal.
   */
  const handleAddAutomation = useCallback(() => {
    setAutomationEditorMode('create');
    setEditingAutomation(buildNewRule());
    setAutomationEditorOpen(true);
  }, [buildNewRule]);

  /**
   * handleEditAutomation
   *
   * Opens the edit-automation modal.
   *
   * @param {object|null} nextRule - The automation rule to edit.
   */
  const handleEditAutomation = useCallback((nextRule) => {
    if (!nextRule) return;
    if (isSystemTypeAutomation(nextRule)) return;

    setAutomationEditorMode('edit');
    setEditingAutomation(nextRule);
    setAutomationEditorOpen(true);
  }, []);

  /**
   * handleReorderView
   *
   * Persists a sparse position update after a drag reorder in the Views tab.
   *
   * @param {string} draggedViewId - The moved view key.
   * @param {string} targetViewId - The drop target view key.
   *
   * @returns {Promise<void>} Resolves when the new view position is saved.
   */
  const handleReorderView = useCallback(async (draggedViewId, targetViewId) => {
    const draggedView = (Array.isArray(views) ? views : []).find((view) => (
      String(view?.key || view?.id || '').trim() === String(draggedViewId || '').trim()
    ));
    if (isSystemTypeView(draggedView)) return;

    const nextPosition = getMovedViewPosition(views, draggedViewId, targetViewId);
    if (nextPosition == null) return;

    try {
      await updateView(draggedViewId, {
        position: nextPosition,
      });
    } catch (error) {
      toast.error(error?.message || 'Could not reorder views.');
      throw error;
    }
  }, [views]);

  /**
   * handleSaveAutomation
   *
   * Persists a single automation rule update from the editor modal.
   *
   * @param {object|null} nextRule - Normalized automation rule draft.
   *
   * @returns {Promise<boolean>} Whether the save succeeded.
   */
  const handleSaveAutomation = useCallback(async (nextRule) => {
    return saveRule(nextRule);
  }, [saveRule]);

  /**
   * handleDeleteAutomation
   *
   * Deletes a single automation rule from the database.
   *
   * @param {string} ruleKey - Automation rule key to delete.
   *
   * @returns {Promise<boolean>} Whether the delete succeeded.
   */
  const handleDeleteAutomation = useCallback(async (ruleKey) => {
    return deleteRule(ruleKey);
  }, [deleteRule]);

  /**
   * persistRollups
   *
   * Saves the current rollups array to the active type.
   *
   * @param {object[]} nextRollups - Next rollup definitions to persist.
   * @param {object} [options] - Persistence options.
   * @param {string} [options.successMessage='Rollups updated.'] - Toast copy shown when save succeeds.
   * @param {boolean} [options.showSuccessToast=true] - Whether to show a success toast.
   *
   * @returns {Promise<boolean>} Whether the save succeeded.
   */
  const persistRollups = useCallback(async (nextRollups, {
    successMessage = 'Rollups updated.',
    showSuccessToast = true,
  } = {}) => {
    if (!effectiveTypeKey) return false;

    const normalizedRollups = normalizeRollupDefinitions(nextRollups, {
      typeKey: effectiveTypeKey,
    });

    try {
      await updateNodeTypeRollups({
        baseKey: baseKey || null,
        typeKey: effectiveTypeKey,
        rollups: normalizedRollups,
      });
      setRollups(normalizedRollups);
      if (showSuccessToast && successMessage) {
        toast.success(successMessage);
      }
      return true;
    } catch (error) {
      toast.error(error?.message || 'Could not update rollups.');
      return false;
    }
  }, [effectiveTypeKey, baseKey]);

  /**
   * handleAddRollup
   *
   * Opens the create-rollup editor.
   */
  const handleAddRollup = useCallback(() => {
    setEditingRollup(null);
    setRollupEditorOpen(true);
  }, []);

  /**
   * handleEditRollup
   *
   * Opens the edit-rollup editor.
   *
   * @param {object|null} nextRollup - Rollup to edit.
   */
  const handleEditRollup = useCallback((nextRollup) => {
    if (!nextRollup) return;
    setEditingRollup(nextRollup);
    setRollupEditorOpen(true);
  }, []);

  /**
   * handleSaveRollup
   *
   * Persists a single rollup draft from the editor modal.
   *
   * @param {object|null} nextRollup - Normalized rollup draft.
   *
   * @returns {Promise<boolean>} Whether the save succeeded.
   */
  const handleSaveRollup = useCallback(async (nextRollup) => {
    const normalizedKey = String(nextRollup?.key || '').trim();
    if (!normalizedKey) return false;

    const previousRollups = normalizeRollupDefinitions(rollups, {
      typeKey: effectiveTypeKey,
    });
    const existingIndex = previousRollups.findIndex((rollup) => rollup?.key === normalizedKey);
    const existingRollup = existingIndex >= 0 ? previousRollups[existingIndex] : null;
    const nextRollupWithPosition = {
      ...nextRollup,
      position: existingRollup ? existingRollup.position : previousRollups.length,
    };
    const nextRollups = existingIndex >= 0
      ? previousRollups.map((rollup) => rollup?.key === normalizedKey ? nextRollupWithPosition : rollup)
      : [...previousRollups, nextRollupWithPosition];

    const didSave = await persistRollups(nextRollups, {
      successMessage: existingIndex >= 0 ? 'Rollup updated.' : 'Rollup created.',
    });
    if (didSave) setEditingRollup(null);
    return didSave;
  }, [effectiveTypeKey, persistRollups, rollups]);

  /**
   * handleDeleteRollup
   *
   * Deletes one rollup definition from the active type.
   *
   * @param {string} rollupKey - Rollup key to remove.
   *
   * @returns {Promise<boolean>} Whether the delete succeeded.
   */
  const handleDeleteRollup = useCallback(async (rollupKey) => {
    const normalizedKey = String(rollupKey || '').trim();
    if (!normalizedKey) return false;

    const nextRollups = normalizeRollupDefinitions(rollups, {
      typeKey: effectiveTypeKey,
    }).filter((rollup) => rollup?.key !== normalizedKey);

    const didDelete = await persistRollups(nextRollups, {
      successMessage: 'Rollup deleted.',
    });
    if (didDelete) setEditingRollup(null);
    return didDelete;
  }, [effectiveTypeKey, persistRollups, rollups]);

  /**
   * handleReorderRollup
   *
   * Persists a sparse position update after drag reorder in the Rollups tab.
   *
   * @param {string} draggedRollupId - The moved rollup key.
   * @param {string} targetRollupId - The drop target rollup key.
   *
   * @returns {Promise<void>} Resolves when the reordered rollups are saved.
   */
  const handleReorderRollup = useCallback(async (draggedRollupId, targetRollupId) => {
    const previousRollups = normalizeRollupDefinitions(rollups, {
      typeKey: effectiveTypeKey,
    });
    const draggedIndex = previousRollups.findIndex((rollup) => rollup?.key === draggedRollupId);
    const targetIndex = previousRollups.findIndex((rollup) => rollup?.key === targetRollupId);
    if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) return;

    const nextRollups = [...previousRollups];
    const [movedRollup] = nextRollups.splice(draggedIndex, 1);
    nextRollups.splice(targetIndex, 0, movedRollup);

    const reorderedRollups = nextRollups.map((rollup, index) => ({
      ...rollup,
      position: index,
    }));

    const didSave = await persistRollups(reorderedRollups, {
      showSuccessToast: false,
    });
    if (!didSave) throw new Error('Could not reorder rollups.');
  }, [effectiveTypeKey, persistRollups, rollups]);

  return {
    modalUiState: {
      selectedTab,
      settingsTabs,
      typeTitlePlural,
    },
    aboutTabState: {
      iconKey,
      handleIconKeyChange,
      saving,
      deleting,
      canSave,
      label,
      setLabel,
      saveError,
      duplicateState,
      handleSave,
      labelPlural,
      handleLabelPluralChange,
      description,
      setDescription,
      defaultView,
      setDefaultView,
      showPeekOnOpen,
      setShowPeekOnOpen,
      allowTopLevelCreate,
      setAllowTopLevelCreate,
      showInMenu,
      setShowInMenu,
      showInAddMenu,
      setShowInAddMenu,
      appearAs,
      setAppearAs,
      schemaFields: resolvedProperties,
      hasOverview: typeHasCollectionReports(typeKey),
      canDelete,
      handleOpenDeleteConfirm,
      allowImport: true,
    },
    relatedTabState: {
      suggestedChildren,
      setSuggestedChildren,
      typeOptions: relatedTypeOptions,
      hostTypeKey: effectiveTypeKey,
      saving: savingRelated,
      canSave: canSaveRelated,
      handleSave: handleSaveRelated,
    },
    viewGroupsTabState: {
      viewGroups,
      setViewGroups,
      saving: savingViewGroups,
      canSave: canSaveViewGroups,
      handleSave: handleSaveViewGroups,
    },
    viewsTabState: {
      onAddView: handleAddView,
      onEditView: handleEditView,
      onReorderView: handleReorderView,
      lockSystemViews: true,
    },
    formsTabState: {
      forms,
      isSaving: isFormSaving,
      onAddForm: handleAddForm,
      onDesignForm: openFormDesign,
      onReorderForm: handleReorderForm,
      onSettingsForm: openFormSettings,
      lockSystemForms: true,
    },
    rollupsTabState: {
      rollups,
      onAddRollup: handleAddRollup,
      onEditRollup: handleEditRollup,
      onReorderRollup: handleReorderRollup,
    },
    missionsTabState: {
      isSaving: isMissionSaving,
      missionTemplates,
      onAddMissionTemplate: handleAddMissionTemplate,
      onDesignMissionTemplate: handleDesignMissionTemplate,
      onReorderMissionTemplate: handleReorderMissionTemplate,
    },
    automationsTabState: {
      rules: sortedRules,
      fieldOptions,
      onAddAutomation: handleAddAutomation,
      onEditAutomation: handleEditAutomation,
      lockSystemAutomations: true,
    },
    overlaysState: {
      viewEditorOpen,
      setViewEditorOpen,
      viewEditorMode,
      editingView,
      viewGroups: savedViewGroups,
      handleViewSaved,
      automationEditorOpen,
      setAutomationEditorOpen,
      editingAutomation,
      automationEditorMode,
      isAutomationSaving,
      fieldOptions,
      ruleFieldOptions,
      fieldTypeByKey,
      fieldEnumOptionsByKey,
      handleSaveAutomation,
      handleDeleteAutomation,
      missionTemplateEditorOpen,
      setMissionTemplateEditorOpen,
      missionTemplateEditorMode,
      selectedMissionTemplateKey,
      isMissionSaving,
      missionTitle,
      missionDescription,
      missionIconKey,
      missionMetaError,
      canSaveMissionMeta,
      setMissionTitle,
      setMissionDescription,
      setMissionIconKey,
      handleSaveMissionMeta,
      handleDeleteMissionTemplate,
      handleDesignMissionTemplate,
      handleSaveMissionDesign,
      missionDesignOpen,
      setMissionDesignOpen,
      closeMissionDesign,
      selectedMissionTemplate,
      handleAddMissionTask,
      handleEditMissionTask,
      handleReorderMissionTask,
      missionEditorOpen,
      setMissionEditorOpen,
      missionTasks,
      missionEdges,
      editingMissionTask,
      resolvedProperties,
      nodeTypeOptions,
      handleSaveMissionTask,
      handleDeleteMissionTask,
      rollupEditorOpen,
      setRollupEditorOpen,
      editingRollup,
      effectiveTypeKey,
      handleSaveRollup,
      handleDeleteRollup,
      deleteConfirmOpen,
      setDeleteConfirmOpen,
      deleteConfirmMessage,
      deleteFinalConfirmOpen,
      setDeleteFinalConfirmOpen,
      handleDelete,
      formDesignOpen,
      closeFormDesign,
      editingForm,
      formsPreviewNodeKey,
      formCreateModalOpen,
      setFormCreateModalOpen,
      formCreateTitleDraft,
      setFormCreateTitleDraft,
      isFormCreating,
      handleCreateForm,
      formSettingsOpen,
      setFormSettingsOpen,
      formSettingsDraft,
      setFormSettingsDraft,
      canSaveFormSettings,
      handleSaveFormSettings,
      handleDeleteForm,
      isFormSaving,
    },
  };
}